import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  CheckCircle, AlertTriangle, FileText, Building, Home, Car,
  Shield, Pen, Clock, DollarSign, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LenderConsentPage = () => {
  const { agreementId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agreement, setAgreement] = useState(null);
  const [documentType, setDocumentType] = useState(null);
  const [error, setError] = useState(null);
  const [consentSubmitted, setConsentSubmitted] = useState(false);
  
  const [consentForm, setConsentForm] = useState({
    authorized_by: '',
    title: '',
    company: '',
    agreed_to_terms: false
  });

  useEffect(() => {
    if (agreementId && token) {
      fetchAgreementDetails();
    } else {
      setError('Invalid consent link. Please check the link in your email.');
      setLoading(false);
    }
  }, [agreementId, token]);

  const fetchAgreementDetails = async () => {
    try {
      const res = await fetch(`${API}/public/novation/${agreementId}?token=${token}`);
      const data = await res.json();
      
      if (res.ok) {
        setAgreement(data.agreement);
        setDocumentType(data.document_type);
        
        if (data.agreement.lender_consent_received) {
          setConsentSubmitted(true);
        }
      } else {
        setError(data.error || 'Failed to load agreement details');
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitConsent = async () => {
    if (!consentForm.authorized_by || !consentForm.agreed_to_terms) {
      toast.error('Please fill in your name and agree to the terms');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/public/novation/${agreementId}/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          ...consentForm
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        setConsentSubmitted(true);
        toast.success('Consent submitted successfully!');
      } else {
        toast.error(data.error || 'Failed to submit consent');
      }
    } catch (err) {
      toast.error('Failed to submit consent. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#b8860b] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading agreement details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-[#0d1d35] border-red-500/50">
          <CardContent className="pt-8 text-center">
            <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Access Error</h2>
            <p className="text-gray-400 mb-6">{error}</p>
            <p className="text-sm text-gray-500">
              If you believe this is an error, please contact the borrower or platform support.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (consentSubmitted) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center p-4">
        <Card className="max-w-lg w-full bg-[#0d1d35] border-green-500/50">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Consent Recorded</h2>
            <p className="text-gray-400 mb-6">
              Thank you for providing your consent. The borrower has been notified and the 
              novation agreement will proceed to the next stage.
            </p>
            <div className="p-4 bg-[#0a1628] rounded-lg text-left">
              <p className="text-xs text-gray-500 mb-2">Agreement ID</p>
              <p className="text-white font-mono text-sm">{agreementId}</p>
            </div>
            <p className="text-xs text-gray-500 mt-6">
              A confirmation email will be sent to you shortly. You may close this page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isMortgage = documentType === 'mortgage';
  const Icon = isMortgage ? Home : Car;
  const partyLabel = isMortgage ? 'Lender' : 'Creditor';

  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Header */}
      <header className="bg-[#0d1d35] border-b border-[#1a2d4a]">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="text-center">
            <h1 className="text-2xl font-light text-white tracking-wider mb-1">BETHEREUM</h1>
            <p className="text-xs text-[#b8860b] tracking-widest">PRIVATE WEALTH MANAGEMENT</p>
          </div>
        </div>
      </header>

      {/* Alert Banner */}
      <div className="bg-gradient-to-r from-yellow-600 to-yellow-500">
        <div className="max-w-4xl mx-auto px-4 py-3 text-center">
          <p className="text-sm font-medium text-black">
            ⚠️ {partyLabel.toUpperCase()} CONSENT REQUEST - ACTION REQUIRED
          </p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Agreement Summary */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a] mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Icon className="w-5 h-5 text-[#b8860b]" />
              {isMortgage ? 'Mortgage' : 'Auto Loan'} Novation Agreement
            </CardTitle>
            <CardDescription>
              Agreement ID: {agreementId}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-300 mb-6">
              <strong className="text-white">{agreement?.borrower_name}</strong> has initiated a 
              Novation & Settlement Agreement to restructure their existing obligation with your institution.
              <strong className="text-yellow-400"> Your consent is required</strong> for this transaction to proceed.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Left Column - Original Details */}
              <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                <h4 className="text-xs text-[#b8860b] uppercase tracking-wider mb-3">
                  {isMortgage ? 'Property' : 'Vehicle'} Details
                </h4>
                {isMortgage ? (
                  <p className="text-white text-sm">{agreement?.property_address}</p>
                ) : (
                  <>
                    <p className="text-white text-sm">{agreement?.vehicle_description}</p>
                    <p className="text-gray-500 text-xs font-mono mt-1">VIN: {agreement?.vehicle_vin}</p>
                  </>
                )}
              </div>

              {/* Right Column - Financial Details */}
              <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                <h4 className="text-xs text-[#b8860b] uppercase tracking-wider mb-3">Settlement Amount</h4>
                <p className="text-2xl font-bold text-[#b8860b]">
                  ${parseFloat(isMortgage ? agreement?.payoff_amount : agreement?.settlement_amount || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  To be paid upon execution
                </p>
              </div>
            </div>

            {/* New Terms Summary */}
            <div className="mt-4 p-4 bg-gradient-to-r from-[#0a1628] to-[#1a2d4a] rounded-lg border border-[#b8860b]/30">
              <h4 className="text-xs text-[#b8860b] uppercase tracking-wider mb-3">
                New Treasury-Indexed Obligation Terms
              </h4>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-gray-500 text-xs">Principal</p>
                  <p className="text-white font-bold">${parseFloat(agreement?.new_principal || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Indexed APR</p>
                  <p className="text-white font-bold">{agreement?.new_indexed_apr}%</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Term</p>
                  <p className="text-white font-bold">
                    {isMortgage ? Math.floor((agreement?.new_term_months || 0) / 12) + ' years' : (agreement?.new_term_months || 0) + ' mo'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">New Payment</p>
                  <p className="text-[#b8860b] font-bold">${parseFloat(agreement?.new_monthly_payment || 0).toLocaleString()}/mo</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Consent Form */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Pen className="w-5 h-5 text-[#b8860b]" />
              Provide Your Consent
            </CardTitle>
            <CardDescription>
              By signing below, you consent to the settlement of the existing obligation and 
              release of your security interest upon receipt of the settlement amount.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Important Notice */}
            <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-200">
                  <strong>Important:</strong> This is a legally binding electronic consent. Upon execution:
                  <ul className="list-disc ml-4 mt-2 text-yellow-200/80">
                    <li>You will receive the settlement amount via wire transfer</li>
                    <li>You agree to release your lien/security interest</li>
                    <li>The borrower's obligation to you will be fully discharged</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider">
                  Authorized Signatory Name *
                </label>
                <Input
                  value={consentForm.authorized_by}
                  onChange={(e) => setConsentForm(prev => ({ ...prev, authorized_by: e.target.value }))}
                  placeholder="Full legal name"
                  className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider">
                  Title / Position
                </label>
                <Input
                  value={consentForm.title}
                  onChange={(e) => setConsentForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Vice President, Loan Officer"
                  className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-gray-400 uppercase tracking-wider">
                  Company / Institution
                </label>
                <Input
                  value={consentForm.company}
                  onChange={(e) => setConsentForm(prev => ({ ...prev, company: e.target.value }))}
                  placeholder={isMortgage ? agreement?.original_lender_name : agreement?.original_creditor_name}
                  className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                />
              </div>
            </div>

            {/* Terms Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
              <input
                type="checkbox"
                checked={consentForm.agreed_to_terms}
                onChange={(e) => setConsentForm(prev => ({ ...prev, agreed_to_terms: e.target.checked }))}
                className="mt-1"
              />
              <span className="text-sm text-gray-400">
                I am authorized to provide consent on behalf of {isMortgage ? agreement?.original_lender_name : agreement?.original_creditor_name}. 
                I have reviewed this Novation Agreement and consent to the settlement terms. I understand this 
                constitutes a legally binding electronic signature under the ESIGN Act.
              </span>
            </label>

            {/* Submit Button */}
            <Button
              onClick={handleSubmitConsent}
              disabled={submitting || !consentForm.authorized_by || !consentForm.agreed_to_terms}
              className="w-full bg-[#b8860b] hover:bg-[#9a7209] py-6 text-lg"
            >
              {submitting ? (
                'Submitting Consent...'
              ) : (
                <>
                  <Pen className="w-5 h-5 mr-2" />
                  Submit Consent & Electronic Signature
                </>
              )}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              By clicking submit, you acknowledge that this is your electronic signature and you 
              agree to be bound by the terms of this agreement.
            </p>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-gray-500">
          <p>© {new Date().getFullYear()} Bethereum Private Wealth Management. All rights reserved.</p>
          <p className="mt-1">This is a secure, encrypted connection.</p>
        </div>
      </main>
    </div>
  );
};

export default LenderConsentPage;
